import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../api/client'
import useDebounce from '../hooks/useDebounce'
import { colors, font, text, radius, sx } from '../design/tokens'

// ⌘K / Ctrl+K — búsqueda global + saltos rápidos desde cualquier pantalla.
// Busca en /api/buscar (clientes, NV, remitos, recibos, presupuestos, artículos
// — vuela con los índices trgm) y ofrece acciones de navegación fijas.

const ACCIONES = [
  { titulo: '+ Nueva nota de venta', sub: 'Ventas', ruta: '/notas-venta/nueva' },
  { titulo: '+ Nuevo presupuesto', sub: 'Ventas', ruta: '/presupuestos/nuevo' },
  { titulo: '+ Nueva cobranza', sub: 'Ventas', ruta: '/pagos/nuevo' },
  { titulo: '+ Nuevo remito libre', sub: 'Logística', ruta: '/remitos/nuevo' },
  { titulo: 'Asignación', sub: 'Logística', ruta: '/asignaciones' },
  { titulo: 'Distribución', sub: 'Logística', ruta: '/distribucion' },
  { titulo: 'Tablero de dirección', sub: 'BI', ruta: '/bi' },
  { titulo: 'Historia de clientes', sub: 'Clientes', ruta: '/clientes/historia' },
  { titulo: 'Monitor Presea', sub: 'Sistema', ruta: '/integraciones/presea' },
]

const norm = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')

export default function CommandPalette() {
  const navigate = useNavigate()
  const [abierto, setAbierto] = useState(false)
  const [q, setQ] = useState('')
  const [grupos, setGrupos] = useState([])
  const [sel, setSel] = useState(0)
  const [buscando, setBuscando] = useState(false)
  const inputRef = useRef(null)
  const qBuscar = useDebounce(q, 250)

  // ⌘K / Ctrl+K abre (reseteando el estado acá, en el handler); Escape cierra.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setAbierto(a => {
          if (!a) {
            setQ(''); setGrupos([]); setSel(0)
            setTimeout(() => inputRef.current?.focus(), 30)
          }
          return !a
        })
      }
      if (e.key === 'Escape') setAbierto(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  useEffect(() => {
    if (!abierto || qBuscar.trim().length < 2) return
    let vivo = true
    setBuscando(true)
    api.get('/buscar', { params: { q: qBuscar.trim() } })
      .then(({ data }) => { if (vivo) { setGrupos(data.grupos || []); setSel(0) } })
      .catch(() => { if (vivo) setGrupos([]) })
      .finally(() => { if (vivo) setBuscando(false) })
    return () => { vivo = false }
  }, [qBuscar, abierto])

  const escribir = (valor) => {
    setQ(valor)
    setSel(0)
    if (valor.trim().length < 2) setGrupos([])
  }

  // Lista plana para navegar con flechas (acciones + resultados), con el header
  // de grupo ya resuelto (nada de mutar variables durante el render).
  const items = useMemo(() => {
    const acciones = q.trim().length > 0
      ? ACCIONES.filter(a => norm(a.titulo).includes(norm(q)))
      : ACCIONES
    const flat = acciones.map(a => ({ ...a, grupo: 'Ir a' }))
    for (const g of grupos) {
      for (const it of g.items) flat.push({ ...it, grupo: g.label })
    }
    return flat.map((item, i) => ({
      ...item,
      header: i === 0 || flat[i - 1].grupo !== item.grupo ? item.grupo : null,
    }))
  }, [q, grupos])

  const elegir = useCallback((item) => {
    if (!item) return
    setAbierto(false)
    navigate(item.ruta, item.state ? { state: item.state } : undefined)
  }, [navigate])

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setSel(s => Math.min(s + 1, items.length - 1)) }
    if (e.key === 'ArrowUp') { e.preventDefault(); setSel(s => Math.max(s - 1, 0)) }
    if (e.key === 'Enter') { e.preventDefault(); elegir(items[sel]) }
  }

  if (!abierto) return null

  return (
    <div style={st.overlay} onMouseDown={() => setAbierto(false)}>
      <div style={st.panel} onMouseDown={e => e.stopPropagation()}>
        <input
          ref={inputRef}
          value={q}
          onChange={e => escribir(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Buscar cliente, NV, remito, recibo… o adónde ir"
          style={st.input}
        />
        <div style={st.lista}>
          {items.length === 0 && (
            <div style={st.vacio}>
              {buscando ? 'Buscando…' : q.trim().length >= 2 ? 'Sin resultados.' : 'Tipeá para buscar (mínimo 2 letras).'}
            </div>
          )}
          {items.map((item, i) => (
            <div key={`${item.grupo}-${item.ruta}-${item.titulo}`}>
              {item.header && <div style={st.grupoHdr}>{item.header}</div>}
              <div
                style={{ ...st.item, ...(i === sel ? st.itemSel : {}) }}
                onMouseEnter={() => setSel(i)}
                onClick={() => elegir(item)}
              >
                <span style={st.itemTitulo}>{item.titulo}</span>
                {item.sub && <span style={st.itemSub}>{item.sub}</span>}
              </div>
            </div>
          ))}
        </div>
        <div style={st.footer}>
          <span>↑↓ navegar · Enter abrir · Esc cerrar</span>
          <span style={sx.mono}>⌘K</span>
        </div>
      </div>
    </div>
  )
}

const st = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.45)',
    display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
    paddingTop: '14vh', zIndex: 1000,
  },
  panel: {
    width: 620, maxWidth: '92vw', background: colors.bg,
    border: `1px solid ${colors.border}`, borderRadius: radius.md,
    boxShadow: '0 20px 60px rgba(15, 23, 42, 0.25)', overflow: 'hidden',
    fontFamily: font.sans,
  },
  input: {
    width: '100%', padding: '14px 16px', border: 'none', outline: 'none',
    borderBottom: `1px solid ${colors.border}`, fontSize: 15,
    fontFamily: font.sans, color: colors.text, boxSizing: 'border-box',
  },
  // Altura FIJA (no maxHeight): el panel no cambia de tamaño mientras se
  // tipea — los resultados scrollean adentro (regla Martin 14 ago).
  lista: { height: '52vh', overflowY: 'auto', padding: '6px 0' },
  vacio: { padding: '18px 16px', color: colors.textMeta, fontSize: text.sec },
  grupoHdr: {
    padding: '8px 16px 4px', fontSize: text.meta, fontWeight: 600,
    color: colors.textMeta, textTransform: 'uppercase', letterSpacing: '0.05em',
  },
  item: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
    gap: 12, padding: '8px 16px', cursor: 'pointer',
  },
  itemSel: { background: '#f1f5f9' },
  itemTitulo: { fontSize: text.body, color: colors.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  itemSub: { fontSize: text.sec, color: colors.textMeta, flexShrink: 0, whiteSpace: 'nowrap' },
  footer: {
    display: 'flex', justifyContent: 'space-between', padding: '8px 16px',
    borderTop: `1px solid ${colors.borderSoft}`, fontSize: text.meta, color: colors.textMeta,
    background: colors.bgSoft,
  },
}
